import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, Shield, TrendingUp } from "lucide-react";

export default function About() {
  return (
    <div data-testid="page-about">
      {/* HEADER */}
      <section className="bg-[#0A1F44] py-24 text-white lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">About Accrue Metrics</div>
              <h1 className="mt-4 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
                Built by professionals.
                <br />
                <span className="italic text-[#C9A227]">Engineered for firms.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg leading-relaxed text-white/75">
                We started Accrue Metrics because every CPA firm we worked with was drowning in
                the same problem: too much volume, not enough hours, and no good way to scale
                without compromising quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE + STORY */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 -rotate-1 border border-[#C9A227]/40" />
              <img
                src="https://images.pexels.com/photos/7433840/pexels-photo-7433840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Accrue Metrics team"
                className="relative h-full w-full rounded-sm object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">Our story</div>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-[#0A1F44]">
              The back office, productized.
            </h2>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-[#475467]">
              <p>
                Accrue Metrics was founded in 2021 by operations leaders who
                saw firms paying premium rates for talent who spent 70% of their day
                on data entry, reconciliations, and chasing client documents.
              </p>
              <p>
                We built Accrue Metrics to fix that. We trained a team of accountants to your standards,
                automated the workflows that kill margin, and layered AI agents on top — but
                always under human review. Today, we power the back office of more than 60 CPA
                firms ranging from solo practitioners to top-200 regional firms.
              </p>
              <p>
                Our promise is simple: your partners get their hours back, your clients see
                better service, and nothing about the relationship changes from their side. We
                stay invisible — by design.
              </p>
            </div>
          </div>
        </div>
      </section>

{/* CORE TEAM */}
<section className="bg-white py-24 lg:py-32">
  <div className="mx-auto max-w-7xl px-6 lg:px-8">

    {/* Heading */}
    <div className="text-center mb-8">
      <h2 className="font-serif text-4xl font-medium text-[#0A1F44]">
        Who’s Behind Accrue Metrics
      </h2>
      <div className="mt-4 flex items-center justify-center gap-4">
        <div className="h-[1px] w-16 bg-[#C9A227]" />
        <div className="h-[1px] w-16 bg-[#C9A227]" />
      </div>
    </div>

    {/* Team Grid */}
    <div className="grid gap-16 lg:grid-cols-1 justify-items-center">

      {/* Member 1 */}
      <div className="flex flex-col items-center text-center max-w-xl">
        <img
          src="/team1.jpg"
          alt="Tannuj Jain"
          className="w-48 h-auto object-contain rounded-sm"
        />

        <div>
          <h3 className="font-serif text-2xl font-semibold text-[#0A1F44]">
            Tannuj Jain
          </h3>
          <p className="text-[#C9A227] mt-1 font-medium">
            Finance & Operations
          </p>

          <ul className="mt-4 space-y-3 text-sm text-[#475467]">
            <li>• Chartered Accountant with experience across financial reporting, bookkeeping, reconciliations, and process optimization.</li>
            <li>• Builds structured finance operations that improve accuracy, consistency, and scalability.</li>
            <li>• Designs efficient systems and workflows to deliver reliable, on-time execution.</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</section>


      {/* VALUES */}
      <section className="bg-[#FAFAFB] py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">What we believe</div>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-5xl">
              Four principles that govern every engagement.
            </h2>
          </div>

          <div className="mt-14 grid gap-px overflow-hidden rounded-sm border border-[#E5E7EB] bg-[#E5E7EB] md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Target,
                title: "Accuracy first",
                body: "Every reconciliation is double-reviewed. Every AI output passes a human checkpoint. There is no 'fast' that excuses 'wrong'.",
              },
              {
                icon: Users,
                title: "Your firm's standards",
                body: "We adapt to your chart of accounts, your review workflow, and your client communication style. We don't ask you to adapt to us.",
              },
              {
                icon: Shield,
                title: "Quiet by default",
                body: "Your clients should never know we exist. We work in your tools, under your domain, with your branding — invisibly.",
              },
              {
                icon: TrendingUp,
                title: "Measured by hours",
                body: "Every quarter we report on hours returned to your firm. If we're not saving you time, we're not doing our job.",
              },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-white p-10 transition-colors hover:bg-[#FAFAFB]">
                  <Icon className="h-7 w-7 text-[#C9A227]" />
                  <h3 className="mt-6 font-serif text-2xl font-semibold text-[#0A1F44]">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#475467]">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LEADERSHIP STAT BLOCK */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.22em] text-[#C9A227]">By the numbers</div>
              <h2 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-[#0A1F44] sm:text-5xl">
                A back office at scale.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-[#475467]">
                Over the last four years our team has grown to 80+ accountants and engineers
                across delivery centers in the U.S. and India — all reporting into one global
                quality function.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-[#E5E7EB] bg-[#E5E7EB] lg:col-span-7">
              {[
                { v: "80+", l: "Team members" },
                { v: "60+", l: "CPA firms served" },
                { v: "10,000+", l: "Recs per month" },
                { v: "4.9/5", l: "Partner NPS" },
              ].map((s) => (
                <div key={s.l} className="bg-white p-10">
                  <div className="font-serif text-5xl font-semibold text-[#0A1F44]">{s.v}</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[#475467]">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* CTA */}
      <section className="bg-[#0A1F44] py-20 text-white lg:py-28">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <h2 className="max-w-2xl font-serif text-3xl font-medium leading-tight text-white sm:text-4xl">
            Want to see how we'd run your back office?
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="hover-lift inline-flex items-center gap-3 rounded-sm bg-[#C9A227] px-7 py-4 text-sm font-semibold text-[#0A1F44] hover:bg-[#B38F22]"
          >
            Book a Discovery Call
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
